import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Zap, Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const { data } = await axios.get(`${API}/invitations/${token}`);
        setInvitation(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Inbjudan hittades inte eller har gått ut');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    }
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/invitations/${token}/accept`, {}, { withCredentials: true });
      setAccepted(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Kunde inte acceptera inbjudan');
    } finally {
      setAccepting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-zinc-800 mb-6">
          <Zap className="w-8 h-8 text-zinc-100" strokeWidth={1.5} />
        </div>

        {error ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-400">{error}</p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="bg-zinc-100 text-zinc-900 hover:bg-white"
            >
              Gå till inloggning
            </Button>
          </div>
        ) : accepted ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-emerald-400">Inbjudan accepterad!</p>
              <p className="text-zinc-500 text-sm mt-2">Omdirigerar till dashboard...</p>
            </div>
          </div>
        ) : invitation ? (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold text-zinc-50 font-['Outfit'] tracking-tight mb-2">
                Workspace-inbjudan
              </h1>
              <p className="text-zinc-500">
                <span className="text-zinc-300">{invitation.invited_by_name}</span> har bjudit in dig till
              </p>
              <p className="text-xl text-zinc-100 font-medium mt-2">
                "{invitation.workspace_name}"
              </p>
            </div>

            {!user ? (
              <div className="space-y-3">
                <p className="text-sm text-zinc-500">
                  Logga in eller skapa ett konto med <span className="text-zinc-300">{invitation.invited_email}</span> för att acceptera inbjudan.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate(`/login?redirect=/invite/${token}`)}
                    variant="outline"
                    className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5"
                  >
                    Logga in
                  </Button>
                  <Button
                    onClick={() => navigate(`/register?redirect=/invite/${token}&email=${encodeURIComponent(invitation.invited_email)}`)}
                    className="flex-1 bg-zinc-100 text-zinc-900 hover:bg-white"
                  >
                    Skapa konto
                  </Button>
                </div>
              </div>
            ) : user.email?.toLowerCase() !== invitation.invited_email?.toLowerCase() ? (
              <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-yellow-400 text-sm">
                  Du är inloggad som <span className="font-medium">{user.email}</span>, men inbjudan är för <span className="font-medium">{invitation.invited_email}</span>.
                </p>
                <p className="text-zinc-500 text-xs mt-2">
                  Logga ut och logga in med rätt konto för att acceptera.
                </p>
              </div>
            ) : (
              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-zinc-100 text-zinc-900 hover:bg-white h-12"
                data-testid="accept-invite-btn"
              >
                {accepting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Acceptera inbjudan'
                )}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
